import { EvaSeriesKind } from "constants/charts.enums";
import { DEFAULT_LINE_CHART_OPTIONS } from "entities/charts";
import {
  BoxPlotDisplayMode,
  placesTypeWellsInCategories
} from "entities/charts/boxPlotDisplayMode";
import { TYPEWELL_Z_INDEX } from "entities/charts/eva/translators/constants";
import { ICheckedForecast } from "store/features";
import { TypeWellSeriesData } from "types/charts/typeWellSeries";

import { DataType } from "models/groupBy";

import {
  CrossPlotNormalizationSettings,
  WellDataUnitConversions,
  calculateNormalizationMultiplier,
  canNormalizeTypeWell,
  getCrossPlotFieldValue,
  getTypeWellDisplayName,
  isNormalizableField,
  shouldSkipZeroValue
} from "./typeWellHelpers";

const BOX_PLOT_TYPEWELL_SYMBOL_SCALE = 22;

/**
 * Check if a field is a "Header" field (e.g., Header.ResourcePlay).
 * Header fields contain well metadata that type wells don't have.
 */
export function isHeaderField(property: string): boolean {
  return property.startsWith("Header.");
}

/**
 * Parsed bin range from a box plot category label.
 */
interface BinRange {
  label: string;
  min: number;
  max: number;
  // A category that is a single value rather than a range, so only that value belongs in it.
  exact?: boolean;
}

/**
 * Parse a bin label to extract numeric range.
 * Handles formats like:
 * - "< 1000" or "<1000" → (-Infinity, 1000)
 * - "1000 - 1500" or "1000-1500" → [1000, 1500)
 * - ">= 4000" or ">=4000" or "> 4000" → [4000, Infinity)
 * - "2018" or "-5" → that single value, for group by fields whose categories are bare values
 *
 * A label that is not numeric at all, such as a resource play name or "n/a", has no range
 * and never matches a value. Neither does a negative range like "-10 - -5", where the sign
 * cannot be told apart from the separator, so those go unmatched as well.
 */
export function parseBinLabel(label: string): BinRange | null {
  const trimmed = label.trim();

  // Handle "< N" format (open left)
  const lessThanMatch = trimmed.match(/^<\s*(\d+(?:\.\d+)?)$/);
  if (lessThanMatch) {
    return {
      label,
      min: -Infinity,
      max: parseFloat(lessThanMatch[1])
    };
  }

  // Handle ">= N" or "> N" format (open right)
  const greaterThanMatch = trimmed.match(/^>=?\s*(\d+(?:\.\d+)?)$/);
  if (greaterThanMatch) {
    return {
      label,
      min: parseFloat(greaterThanMatch[1]),
      max: Infinity
    };
  }

  // Handle "N - M" range format
  const rangeMatch = trimmed.match(/^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/);
  if (rangeMatch) {
    return {
      label,
      min: parseFloat(rangeMatch[1]),
      max: parseFloat(rangeMatch[2])
    };
  }

  // Handle quantile format: "min - max (Qx)"
  const quantileMatch = trimmed.match(
    /^(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)\s*\(Q\d+\)$/i
  );
  if (quantileMatch) {
    return {
      label,
      min: parseFloat(quantileMatch[1]),
      max: parseFloat(quantileMatch[2])
    };
  }

  // Handle a bare value, such as a vintage year, signed so a negative category still matches
  const exactMatch = trimmed.match(/^([-+]?\d+(?:\.\d+)?)$/);
  if (exactMatch) {
    const value = parseFloat(exactMatch[1]);
    return {
      label,
      min: value,
      max: value,
      exact: true
    };
  }

  return null;
}

/**
 * Find the bin category that contains a given numeric value.
 * Returns the bin label if found, null otherwise.
 */
export function findBinForValue(value: number, categories: string[]): string | null {
  for (const category of categories) {
    const range = parseBinLabel(category);
    if (!range) continue;

    // Check if value falls within this bin's range
    // Using [min, max) convention (inclusive min, exclusive max)
    // except for the last bin which should include max
    if (range.exact) {
      // Single-value category: only that value belongs in it
      if (value === range.min) return category;
    } else if (range.max === Infinity) {
      // Open right bin: value >= min
      if (value >= range.min) return category;
    } else if (range.min === -Infinity) {
      // Open left bin: value < max
      if (value < range.max) return category;
    } else {
      // Range bin: min <= value < max
      if (value >= range.min && value < range.max) return category;
    }
  }

  // If no exact match, find the closest bin
  // This handles edge cases where value equals the max of a range
  for (const category of categories) {
    const range = parseBinLabel(category);
    if (!range || range.exact) continue;

    if (value === range.max && range.max !== Infinity) {
      const nextBin = categories.find((c) => {
        const r = parseBinLabel(c);
        return r && r.min === value;
      });
      if (nextBin) return nextBin;
      // Otherwise return this bin
      return category;
    }
  }

  return null;
}

export interface BoxPlotSeriesParams {
  typeWells: ICheckedForecast[];
  yProperty: string;
  xProperty: string;
  xDataType?: DataType;
  displayMode: BoxPlotDisplayMode;
  performanceData: Record<string, Record<string, number | null>> | undefined;
  normalization?: CrossPlotNormalizationSettings;
  unitConversions?: WellDataUnitConversions;
  categories?: string[];
}

export interface BoxPlotSeriesResult {
  series: TypeWellSeriesData;
  missingCount: number;
  missingDataCount: number;
  // Whether the series place type wells in x-axis categories (a category label as the X
  // value) rather than spanning the chart as horizontal lines. Decided here, at the branch,
  // so consumers that cannot read a category X (the data table) stay in step with the shape
  // that was actually built instead of guessing from the display mode.
  placedInCategories: boolean;
}

// The two builders below own the series and the counts; which branch ran is the caller's
// answer to give, so they return everything but that flag.
type BoxPlotSeriesShape = Omit<BoxPlotSeriesResult, "placedInCategories">;

/**
 * Whether a type well's X value can ever be matched to one of the x-axis categories.
 * Only a numeric field qualifies: a type well has no header data, a date is only ever its arps
 * start date rather than the date that was asked for, and a text grouping has labels no value
 * can be parsed against. Each of those leaves nowhere to put a diamond, so the type well keeps
 * its horizontal line.
 */
function canResolveCategory(xProperty: string, xDataType?: DataType): boolean {
  if (isHeaderField(xProperty) || xProperty.startsWith("Dates.")) return false;
  return xDataType === "Number" || xDataType === "Integer";
}

/**
 * Build series for box plot type well visualization.
 * - Plain box mode: Returns horizontal markLine series at Y performance values
 * - Jitter or violin mode with an X field no category can be resolved from: Returns
 *   horizontal markLine series, since a type well has no value to place
 * - Jitter or violin mode with no categories yet: Returns horizontal markLine series
 * - Jitter or violin mode otherwise: Returns a diamond per type well in the category its X
 *   value resolves to, falling back to a horizontal markLine for any type well whose value
 *   is missing or matches no category
 */
export function buildBoxPlotSeries(params: BoxPlotSeriesParams): BoxPlotSeriesResult {
  const {
    typeWells,
    yProperty,
    xProperty,
    xDataType,
    displayMode,
    performanceData,
    normalization,
    unitConversions,
    categories
  } = params;

  // Plain box mode, or an X field whose categories a type well can never be matched
  // against: horizontal lines at the Y value.
  // There are also no categories to place a point in until the response arrives: right
  // after switching modes the categories still describe the previous response. Draw the
  // lines meanwhile so the type well stays on the chart.
  const hasCategories = (categories?.length ?? 0) > 0;
  if (
    !placesTypeWellsInCategories(displayMode) ||
    !canResolveCategory(xProperty, xDataType) ||
    !hasCategories
  ) {
    return {
      ...buildBoxPlotMarkLineSeries({
        typeWells,
        yProperty,
        performanceData,
        normalization,
        unitConversions
      }),
      placedInCategories: false
    };
  }

  // A mode that places type wells, with categories to place them in: a diamond each
  return {
    ...buildBoxPlotCategorySeries({
      typeWells,
      yProperty,
      xProperty,
      performanceData,
      normalization,
      unitConversions,
      categories
    }),
    placedInCategories: true
  };
}

interface BoxPlotMarkLineParams {
  typeWells: ICheckedForecast[];
  yProperty: string;
  performanceData: Record<string, Record<string, number | null>> | undefined;
  normalization?: CrossPlotNormalizationSettings;
  unitConversions?: WellDataUnitConversions;
}

/**
 * Build horizontal markLine series: each type well gets a dashed horizontal line at its Y
 * performance value. This is the plain box's only shape, and it is also what jitter and
 * violin fall back to for an individual type well they could not place in a category.
 * Note: When Y is a date or header field, markLines cannot be rendered (returns empty series).
 */
function buildBoxPlotMarkLineSeries(params: BoxPlotMarkLineParams): BoxPlotSeriesShape {
  const { typeWells, yProperty, performanceData, normalization, unitConversions } =
    params;

  // When Y is a date or header field, we can't render meaningful markLines
  // (date Y values don't make visual sense, and type wells don't have header data)
  if (yProperty.startsWith("Dates.") || isHeaderField(yProperty)) {
    return { series: [], missingCount: 0, missingDataCount: typeWells.length };
  }

  const series: TypeWellSeriesData = [];
  let missingCount = 0;
  let missingDataCount = 0;

  // Check if Y field should be normalized
  const shouldNormalizeY =
    normalization?.enabled &&
    normalization?.normalizeTypeWell &&
    isNormalizableField(yProperty);

  for (const tw of typeWells) {
    const perfData = performanceData?.[tw.id];

    let yValue = getCrossPlotFieldValue(tw, yProperty, perfData, unitConversions);
    if (yValue == null || typeof yValue !== "number") {
      missingDataCount++;
      continue;
    }

    // Skip zero values only if the product doesn't exist in the type well
    if (shouldSkipZeroValue(tw, yProperty, yValue)) {
      missingDataCount++;
      continue;
    }

    if (shouldNormalizeY && normalization) {
      const { canNormalize, wellValue } = canNormalizeTypeWell(tw, normalization);

      if (!canNormalize) {
        missingCount++;
        continue;
      }

      // Apply normalization if we have a valid well value
      if (wellValue != null) {
        const normalizeMultiplier = calculateNormalizationMultiplier(
          wellValue,
          normalization
        );
        yValue = yValue * normalizeMultiplier;
      }
    }

    const typeWellName = getTypeWellDisplayName(tw);

    // Create a scatter series with markLine for horizontal line visualization
    // Using scatter (not line) to avoid interfering with box plot's category x-axis
    series.push({
      ...DEFAULT_LINE_CHART_OPTIONS,
      name: typeWellName,
      id: `typewell-boxplot-${tw.id}`,
      originalId: tw.id,
      type: "scatter",
      kind: EvaSeriesKind.TypeWell,
      data: [], // Empty data - the line is drawn via markLine
      symbol: "none",
      z: TYPEWELL_Z_INDEX,
      markLine: {
        silent: false,
        symbol: "none",
        lineStyle: {
          color: tw.color ?? "#000000",
          width: tw.thickness,
          type: "dashed"
        },
        emphasis: {
          lineStyle: {
            width: (tw.thickness ?? 2) + 2,
            opacity: 1
          }
        },
        label: {
          show: false
        },
        data: [{ yAxis: yValue, name: typeWellName }]
      }
    } as TypeWellSeriesData[0]);
  }

  return { series, missingCount, missingDataCount };
}

interface BoxPlotCategoryParams {
  typeWells: ICheckedForecast[];
  yProperty: string;
  xProperty: string;
  performanceData: Record<string, Record<string, number | null>> | undefined;
  normalization?: CrossPlotNormalizationSettings;
  unitConversions?: WellDataUnitConversions;
  categories?: string[];
}

/**
 * Build the diamond series for the modes that place type wells in categories (jitter and
 * violin), one per type well in the category its X value resolves to.
 * Any type well whose X value is missing or matches no category is handed to
 * buildBoxPlotMarkLineSeries instead, so it still shows as a horizontal line.
 */
function buildBoxPlotCategorySeries(params: BoxPlotCategoryParams): BoxPlotSeriesShape {
  const {
    typeWells,
    yProperty,
    xProperty,
    performanceData,
    normalization,
    unitConversions,
    categories = []
  } = params;

  const series: TypeWellSeriesData = [];
  let missingCount = 0;
  let missingDataCount = 0;
  // Type wells with a usable Y value but no category to sit in. They get a horizontal line
  // rather than being dropped, which is what a plain box would have drawn for them anyway.
  const unplaceable: ICheckedForecast[] = [];

  // Check which fields should be normalized (only Y for box plot)
  const shouldNormalizeY =
    normalization?.enabled &&
    normalization?.normalizeTypeWell &&
    isNormalizableField(yProperty);

  for (const tw of typeWells) {
    const perfData = performanceData?.[tw.id];
    const xValue = getCrossPlotFieldValue(tw, xProperty, perfData, unitConversions);
    let yValue = getCrossPlotFieldValue(tw, yProperty, perfData, unitConversions);

    // Without a Y value there is nothing to draw either way
    if (yValue == null || typeof yValue !== "number") {
      missingDataCount++;
      continue;
    }

    // Skip zero Y values only if the product doesn't exist in the type well
    if (shouldSkipZeroValue(tw, yProperty, yValue)) {
      missingDataCount++;
      continue;
    }

    if (xValue == null || typeof xValue !== "number") {
      unplaceable.push(tw);
      continue;
    }

    // Find the category for this X value using numeric range matching
    const category = findBinForValue(xValue, categories);

    if (!category) {
      // The value falls outside every category, so there is nowhere to put the diamond
      unplaceable.push(tw);
      continue;
    }

    // Check if normalization is required but well data is missing
    if (shouldNormalizeY && normalization) {
      const { canNormalize, wellValue } = canNormalizeTypeWell(tw, normalization);

      if (!canNormalize) {
        missingCount++;
        continue;
      }

      if (wellValue != null) {
        const normalizeMultiplier = calculateNormalizationMultiplier(
          wellValue,
          normalization
        );
        yValue = yValue * normalizeMultiplier;
      }
    }

    const typeWellName = getTypeWellDisplayName(tw);
    series.push({
      ...DEFAULT_LINE_CHART_OPTIONS,
      name: typeWellName,
      id: `typewell-boxplot-binned-${tw.id}`,
      originalId: tw.id,
      type: "scatter",
      kind: EvaSeriesKind.TypeWell,
      symbol: "diamond",
      symbolSize: tw.thickness + BOX_PLOT_TYPEWELL_SYMBOL_SCALE,
      itemStyle: {
        color: tw.color ?? "#000000",
        opacity: 1
      },
      emphasis: {
        scale: 1.2,
        itemStyle: {
          opacity: 1
        }
      },
      blur: {
        itemStyle: {
          opacity: 0.8
        }
      },
      z: TYPEWELL_Z_INDEX,
      data: [[category, yValue]]
    } as TypeWellSeriesData[0]);
  }

  if (unplaceable.length > 0) {
    const lines = buildBoxPlotMarkLineSeries({
      typeWells: unplaceable,
      yProperty,
      performanceData,
      normalization,
      unitConversions
    });
    series.push(...lines.series);
    missingCount += lines.missingCount;
    missingDataCount += lines.missingDataCount;
  }

  return { series, missingCount, missingDataCount };
}

/**
 * Extract fields needed for box plot API request.
 * Excludes date fields (use arps start date) and Header fields (type wells don't have them).
 */
export function extractBoxPlotFields(
  yProperty: string | undefined,
  xProperty: string | undefined,
  displayMode: BoxPlotDisplayMode,
  xDataType?: DataType
): string[] {
  const fields: string[] = [];

  if (yProperty && !yProperty.startsWith("Dates.") && !isHeaderField(yProperty)) {
    fields.push(yProperty);
  }

  // Only need the X field when type wells are placed in categories and the field is one that
  // both resolves to a category and has to be fetched: a date comes from the type well's own
  // arps start date, and a field no category can be resolved from would be fetched for
  // nothing — worse, a field the service does not support fails the whole query and takes
  // the Y value down with it, so every type well would vanish
  if (
    placesTypeWellsInCategories(displayMode) &&
    xProperty &&
    xProperty !== yProperty &&
    !xProperty.startsWith("Dates.") &&
    canResolveCategory(xProperty, xDataType)
  ) {
    fields.push(xProperty);
  }

  return fields;
}
